import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, SlidersHorizontal, X } from 'lucide-react';
import { brands } from '../data/brands';
import { searchListings, SearchFilters } from '../lib/search';
import SearchResults from '../components/SearchResults';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';

const PAGE_SIZE = 24;

// The URL is the single source of truth for the search state; every control
// writes to the URL and this parser derives the filters from it. The header
// search bar navigates with ?q=, older links use ?query= — accept both.
function parseParams(searchParams: URLSearchParams): {
  filters: SearchFilters;
  brandIds: string[];
  query: string;
} {
  const query = searchParams.get('query') || searchParams.get('q') || '';
  return {
    query,
    brandIds: searchParams.get('brands')?.split(',').filter(Boolean) || [],
    filters: {
      query: query || undefined,
      condition: searchParams.get('condition') || undefined,
      minPrice: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined,
      maxPrice: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined,
      type: (searchParams.get('type') as 'sale' | 'trade' | 'both') || undefined,
      color: searchParams.get('color') || undefined,
      source: (searchParams.get('source') as 'user' | 'clearancewigs') || undefined,
      fiber: searchParams.get('fiber') || undefined,
      capSize: searchParams.get('capSize') || undefined,
      sort: (searchParams.get('sort') as SearchFilters['sort']) || undefined,
    },
  };
}

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, brandIds: selectedBrands, query: urlQuery } = React.useMemo(
    () => parseParams(searchParams),
    [searchParams],
  );

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [minPriceInput, setMinPriceInput] = useState(filters.minPrice?.toString() || '');
  const [maxPriceInput, setMaxPriceInput] = useState(filters.maxPrice?.toString() || '');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Keep local inputs in sync when the URL changes from outside (header search,
  // back button, shared link).
  useEffect(() => {
    setSearchQuery(urlQuery);
    setMinPriceInput(filters.minPrice?.toString() || '');
    setMaxPriceInput(filters.maxPrice?.toString() || '');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, page]);

  // Debounce the price inputs into the URL (typing "150" = one fetch, not 3).
  const priceDebounce = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (
      minPriceInput === (filters.minPrice?.toString() || '') &&
      maxPriceInput === (filters.maxPrice?.toString() || '')
    ) {
      return;
    }
    clearTimeout(priceDebounce.current);
    priceDebounce.current = setTimeout(() => {
      updateParams({
        minPrice: minPriceInput || undefined,
        maxPrice: maxPriceInput || undefined,
      });
    }, 450);
    return () => clearTimeout(priceDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPriceInput, maxPriceInput]);

  const fetchResults = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const { listings, total, error } = await searchListings(
        { ...filters, brand: selectedBrands },
        { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      );
      if (error) throw error;
      setListings(listings);
      setTotal(total);
    } catch (error) {
      console.error('Error fetching search results:', error);
      setFetchError(true);
      toast.error('Failed to load search results');
    } finally {
      setLoading(false);
    }
  };

  /** Merge changes into the URL params (undefined removes a key). */
  const updateParams = (changes: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    params.delete('q'); // normalize the header-search alias into `query`
    Object.entries(changes).forEach(([k, v]) => {
      if (v === undefined || v === '') params.delete(k);
      else params.set(k, v);
    });
    setPage(0);
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ query: searchQuery.trim() || undefined });
  };

  const handleBrandToggle = (brandId: string) => {
    const newBrands = selectedBrands.includes(brandId)
      ? selectedBrands.filter((b) => b !== brandId)
      : [...selectedBrands, brandId];
    updateParams({ brands: newBrands.length ? newBrands.join(',') : undefined });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setMinPriceInput('');
    setMaxPriceInput('');
    setPage(0);
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters =
    !!urlQuery || selectedBrands.length > 0 ||
    Object.values(filters).some((v) => v !== undefined && v !== urlQuery);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const filterPanel = (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="space-y-6">
        {/* Brand Filter */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Brands</h3>
            {selectedBrands.length > 0 && (
              <button
                onClick={() => updateParams({ brands: undefined })}
                className="text-xs text-rose-600 hover:text-rose-700"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="space-y-2">
            {Object.values(brands).map(brand => (
              <label key={brand.id} className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                  checked={selectedBrands.includes(brand.id)}
                  onChange={() => handleBrandToggle(brand.id)}
                />
                <span className="ml-2 text-sm text-gray-600">{brand.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Price Range</h3>
          <div className="space-y-2">
            <input
              type="number"
              placeholder="Min Price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={minPriceInput}
              onChange={(e) => setMinPriceInput(e.target.value)}
            />
            <input
              type="number"
              placeholder="Max Price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={maxPriceInput}
              onChange={(e) => setMaxPriceInput(e.target.value)}
            />
          </div>
        </div>

        {/* Condition Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Condition</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.condition || ''}
            onChange={(e) => updateParams({ condition: e.target.value || undefined })}
          >
            <option value="">Any Condition</option>
            <option value="new">New with tags</option>
            <option value="like-new">Like new</option>
            <option value="gently-used">Gently used</option>
            <option value="well-used">Well used</option>
          </select>
        </div>

        {/* Color Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Color</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.color || ''}
            onChange={(e) => updateParams({ color: e.target.value || undefined })}
          >
            <option value="">Any Color</option>
            <option value="Black">Black</option>
            <option value="Brunette">Brunette</option>
            <option value="Blonde">Blonde</option>
            <option value="Red">Red</option>
            <option value="Gray/Silver">Gray/Silver</option>
            <option value="White">White</option>
          </select>
        </div>

        {/* Hair Type Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Hair Type</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.fiber || ''}
            onChange={(e) => updateParams({ fiber: e.target.value || undefined })}
          >
            <option value="">Any Hair Type</option>
            <option value="Synthetic">Synthetic</option>
            <option value="Synthetic Heat Friendly">Synthetic Heat Friendly</option>
            <option value="Human Hair">Human Hair</option>
            <option value="Human Hair Blend">Human Hair Blend</option>
          </select>
        </div>

        {/* Cap Size Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Cap Size</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.capSize || ''}
            onChange={(e) => updateParams({ capSize: e.target.value || undefined })}
          >
            <option value="">Any Cap Size</option>
            <option value="Petite">Petite</option>
            <option value="Average">Average</option>
            <option value="Large">Large</option>
          </select>
        </div>

        {/* Source Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Source</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.source || ''}
            onChange={(e) => updateParams({ source: e.target.value || undefined })}
          >
            <option value="">All Listings</option>
            <option value="user">Member listings</option>
            <option value="clearancewigs">ClearanceWigs (outlet &amp; open-box)</option>
          </select>
        </div>

        {/* Listing Type Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Listing Type</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.type || ''}
            onChange={(e) => updateParams({ type: e.target.value || undefined })}
          >
            <option value="">All Types</option>
            <option value="sale">For Sale</option>
            <option value="trade">For Trade</option>
            <option value="both">Sale or Trade</option>
          </select>
        </div>

        {/* Sort Options */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Sort By</h3>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={filters.sort || 'newest'}
            onChange={(e) => updateParams({ sort: e.target.value === 'newest' ? undefined : e.target.value })}
          >
            <option value="newest">Newest First</option>
            <option value="discount">Biggest Discount</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <SEO
        title="Search Wigs"
        description="Search pre-owned and open-box premium wigs by brand, color, condition and price on WigConnection."
        canonical="/search"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search Header */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search wigs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
            <SearchIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </form>

        {/* Mobile filter toggle */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {showMobileFilters ? 'Hide filters' : 'Filters'}
            {hasActiveFilters && <span className="h-2 w-2 rounded-full bg-rose-600" />}
          </button>
          {showMobileFilters && <div className="mt-4">{filterPanel}</div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Filters Sidebar (desktop) */}
          <div className="hidden md:block md:col-span-3">{filterPanel}</div>

          {/* Results */}
          <div className="md:col-span-9">
            {(urlQuery || selectedBrands.length > 0) && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {urlQuery && (
                  <div className="inline-flex items-center bg-gray-200 text-gray-800 rounded-full px-3 py-1 text-sm">
                    <span>&ldquo;{urlQuery}&rdquo;</span>
                    <button
                      onClick={() => { setSearchQuery(''); updateParams({ query: undefined }); }}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {selectedBrands.map(brandId => (
                  <div
                    key={brandId}
                    className="inline-flex items-center bg-rose-100 text-rose-800 rounded-full px-3 py-1 text-sm"
                  >
                    <span>{brands[brandId]?.name || brandId}</span>
                    <button
                      onClick={() => handleBrandToggle(brandId)}
                      className="ml-2 text-rose-600 hover:text-rose-700"
                      aria-label={`Remove ${brands[brandId]?.name || brandId} filter`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-rose-600 hover:text-rose-700 underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}

            {!loading && !fetchError && (
              <p className="mb-4 text-sm text-gray-600">
                {total} {total === 1 ? 'listing' : 'listings'} found
              </p>
            )}

            {fetchError && !loading ? (
              <div className="text-center py-12">
                <p className="text-gray-700 mb-3">Something went wrong loading results.</p>
                <button
                  onClick={fetchResults}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700"
                >
                  Try again
                </button>
              </div>
            ) : (
              <SearchResults
                listings={listings}
                loading={loading}
                onClearFilters={hasActiveFilters ? clearAllFilters : undefined}
              />
            )}

            {/* Pagination */}
            {!loading && total > PAGE_SIZE && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {totalPages} · {total} listings
                </span>
                <button
                  onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Search;
